import React, { useMemo } from 'react';
import { Dropdown, IDropdownOption, TextField, MessageBar, MessageBarType } from '@fluentui/react';
import { AssignmentPreviewDto, TimeSearchRow, TimeUser, Activity } from '../../../../../api/generated/data-contracts';

interface RateSelectionSectionProps {
    selectedUser: TimeUser | null;
    selectedTask: TimeSearchRow | null;
    assignmentInfo: AssignmentPreviewDto | null;
    isLoadingPreview: boolean;
    activities: Activity[];
    selectedActivity: Activity | null;
    setSelectedActivity: (activity: Activity | null) => void;
    customRate: string;
    setCustomRate: (rate: string) => void;
}

export const RateSelectionSection: React.FC<RateSelectionSectionProps> = ({
    selectedUser,
    selectedTask,
    assignmentInfo,
    isLoadingPreview,
    activities,
    selectedActivity,
    setSelectedActivity,
    customRate,
    setCustomRate,
}) => {
    // Activity dropdown options - include "None" option at the beginning
    const activityOptions: IDropdownOption[] = useMemo(() => {
        const options: IDropdownOption[] = [{ key: 'none', text: '(None)' }];

        activities.forEach(activity => {
            options.push({
                key: activity.id,
                text: `${activity.name} - ${activity.rate} ${activity.rateCurrencyCode || ''}`,
            });
        });

        return options;
    }, [activities]);

    // Don't render anything if user and task are not selected
    if (!selectedUser || !selectedTask) {
        return null;
    }

    return (
        <>
            {/* Rate Selection Section Header - Only show when rate is required */}
            {assignmentInfo?.requiresActivityOrRate && !assignmentInfo.existingApprovalActivityId && !assignmentInfo.existingApprovalCustomRate && (
                <MessageBar messageBarType={MessageBarType.info}>
                    Rate Selection Required * - Please select an Activity OR enter a Custom Rate (one of the two is required).
                </MessageBar>
            )}

            {/* Show info when existing activity/rate is locked */}
            {!!(assignmentInfo && (assignmentInfo.existingApprovalActivityId || assignmentInfo.existingApprovalCustomRate)) && (
                <MessageBar messageBarType={MessageBarType.info}>
                    {assignmentInfo.existingApprovalActivityId
                        ? `An existing time entry for this date uses Activity: ${assignmentInfo.existingApprovalActivityName}. This cannot be changed.`
                        : `An existing time entry for this date uses Custom Rate: ${assignmentInfo.existingApprovalCustomRate}. This cannot be changed.`}
                </MessageBar>
            )}

            {/* Activity Selection - Always render to prevent layout shift */}
            {(() => {
                let activityLabel: string;
                if (isLoadingPreview) {
                    activityLabel = 'Activity (Loading...)';
                } else if (assignmentInfo?.requiresActivityOrRate) {
                    activityLabel = 'Activity';
                } else {
                    activityLabel = 'Activity (Optional)';
                }

                return (
                    <Dropdown
                        label={activityLabel}
                        placeholder="Select an activity"
                        options={activityOptions}
                        selectedKey={selectedActivity?.id || 'none'}
                        onChange={(_, option) => {
                            if (option?.key === 'none') {
                                setSelectedActivity(null);
                            } else {
                                const activity = activities.find(a => a.id === option?.key);
                                setSelectedActivity(activity || null);
                                if (activity) {
                                    setCustomRate(''); // Clear custom rate when activity selected
                                }
                            }
                        }}
                        disabled={
                            isLoadingPreview || !!customRate || !!assignmentInfo?.existingApprovalActivityId || !!assignmentInfo?.existingApprovalCustomRate
                        }
                        styles={{ root: { maxWidth: 300 } }}
                    />
                );
            })()}

            {/* Custom Rate Input - Always render to prevent layout shift */}
            {(() => {
                let customRateLabel: string;
                if (isLoadingPreview) {
                    customRateLabel = 'Custom Rate (Loading...)';
                } else if (assignmentInfo?.requiresActivityOrRate) {
                    customRateLabel = 'Custom Rate';
                } else {
                    customRateLabel = 'Custom Rate (Optional)';
                }

                let customRateDescription: string;
                if (isLoadingPreview) {
                    customRateDescription = 'Loading assignment information...';
                } else if (assignmentInfo?.existingApprovalActivityId) {
                    customRateDescription = 'Locked - existing time entry uses an activity';
                } else if (assignmentInfo?.existingApprovalCustomRate) {
                    customRateDescription = 'Locked - existing time entry uses this custom rate';
                } else if (selectedActivity) {
                    customRateDescription = 'Clear the Activity selection to enter a custom rate';
                } else if (assignmentInfo?.rate) {
                    customRateDescription = 'Enter a custom rate to override the assignment rate';
                } else {
                    customRateDescription = 'Enter a custom rate for this correction';
                }

                return (
                    <TextField
                        label={customRateLabel}
                        type="number"
                        value={customRate}
                        onChange={(_, newValue) => {
                            setCustomRate(newValue || '');
                            if (newValue) {
                                setSelectedActivity(null); // Clear activity when custom rate entered
                            }
                        }}
                        disabled={
                            isLoadingPreview ||
                            !!selectedActivity ||
                            !!assignmentInfo?.existingApprovalActivityId ||
                            !!assignmentInfo?.existingApprovalCustomRate
                        }
                        description={customRateDescription}
                        placeholder="Enter rate"
                        styles={{ root: { maxWidth: 200 } }}
                        readOnly={!!assignmentInfo?.existingApprovalActivityId || !!assignmentInfo?.existingApprovalCustomRate}
                    />
                );
            })()}
        </>
    );
};
